import { useRef } from 'react';

import { HorizontalContainer } from '@american-autonomy/ag-ui';

import { MINIMUM_COLUMN_WIDTH, TEST_ID } from './constants';
import type { FunctionComponent, Props, ReactMouseEvent } from './types';

import styles from './styles.module.scss';

const WithResize: FunctionComponent<Props> = ({
    children,
    className = '',
    onClick,
    onResize,
    testId = TEST_ID
}) => {
    const containerRef = useRef<HTMLDivElement>(null);

    const handleMouseDown = (event: ReactMouseEvent<HTMLDivElement, MouseEvent>) => {
        const childElement = containerRef.current;
        const gridContainer = childElement?.parentElement;

        if (!childElement || !gridContainer || !onResize) {
            return;
        }

        event.preventDefault();

        const startX = event.clientX;

        const childrenArray = Array.from(gridContainer.children);
        const childIndex = childrenArray.indexOf(childElement);

        const computedStyle = window.getComputedStyle(gridContainer);
        const initialGridTemplate = computedStyle.getPropertyValue('grid-template-columns');

        const initialColumns = initialGridTemplate.split(' ');
        const totalColumnCount = initialColumns.length;

        const columnIndexToResize = childIndex % totalColumnCount;

        const nextColumnIndex = columnIndexToResize + 1;

        if (nextColumnIndex >= totalColumnCount) {
            return;
        }

        const initialColumnWidths: number[] = [];

        childrenArray.forEach((child, index) => {
            if (index < totalColumnCount) {
                initialColumnWidths.push((child as HTMLElement).offsetWidth);
            }
        });

        const onMouseMove = (moveEvent: globalThis.MouseEvent) => {
            const deltaX = moveEvent.clientX - startX;

            const maxGrowth = initialColumnWidths[nextColumnIndex] - MINIMUM_COLUMN_WIDTH;
            const maxShrink = initialColumnWidths[columnIndexToResize] - MINIMUM_COLUMN_WIDTH;
            const clampedDelta = Math.max(-maxShrink, Math.min(maxGrowth, deltaX));

            const updatedWidths = [...initialColumnWidths];

            updatedWidths[columnIndexToResize] =
                initialColumnWidths[columnIndexToResize] + clampedDelta;
            updatedWidths[nextColumnIndex] = initialColumnWidths[nextColumnIndex] - clampedDelta;

            const pixelValues = updatedWidths.map(width => `${width}px`);

            onResize(pixelValues.join(' '));
        };

        const onMouseUp = () => {
            const actualWidths: number[] = [];

            childrenArray.forEach((child, index) => {
                if (index < totalColumnCount) {
                    actualWidths.push((child as HTMLElement).offsetWidth);
                }
            });

            const frValues = actualWidths.map(
                width => `minmax(${MINIMUM_COLUMN_WIDTH}px, ${width}fr)`
            );

            onResize(frValues.join(' '));

            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };

        window.addEventListener('mousemove', onMouseMove);
        window.addEventListener('mouseup', onMouseUp);
    };

    return (
        <HorizontalContainer
            className={`${className} ${styles.withResize}`}
            onClick={onClick}
            ref={containerRef}
            testId={testId}>
            {children}

            <div className={styles.pull} onMouseDown={handleMouseDown}></div>
        </HorizontalContainer>
    );
};

export default WithResize;
